import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MoreHorizontal, Pencil, PlayCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { features } from "@/lib/features";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CastPersonaDialog } from "@/components/experience/cast-persona-dialog";
import { useCanEdit } from "@/lib/project-access";
import { ScenarioItem } from "@/components/experience/scenario-row";
import type { OutlineListItem } from "./scenarios-tab-types";

interface ScenarioRowItemProps {
  scenario: OutlineListItem;
  projectId: string;
  headingLevel: "h2" | "h3";
}

export function ScenarioRowItem({
  scenario,
  projectId,
  headingLevel: Heading,
}: ScenarioRowItemProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const canEdit = useCanEdit();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [castOpen, setCastOpen] = useState(false);

  const deleteMutation = trpc.outline.delete.useMutation({
    onSuccess: () => {
      toast.success("Scenario deleted");
      void utils.outline.list.invalidate();
      setConfirmingDelete(false);
    },
    onError: (err) => toastTrpcError(err, "Failed to delete scenario"),
  });

  const alreadyCastPersonaIds = (scenario.personaVerdicts ?? []).map(
    (v) => v.personaId,
  );

  return (
    <>
      <ScenarioItem
        variant="full"
        scenario={scenario}
        projectId={projectId}
        headingLevel={Heading}
        actions={
          canEdit ? (
            <div className="flex items-center gap-1.5">
              {features.liveWalkthrough && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs font-medium"
                  asChild
                >
                  <Link
                    to={`/projects/${projectId}/live-walkthrough?scenarioId=${scenario.id}`}
                    aria-label={`Run scenario: ${scenario.name}`}
                  >
                    <PlayCircle className="h-3.5 w-3.5 text-primary" />
                    <span>Run</span>
                  </Link>
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Scenario actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setCastOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Cast another persona
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(
                        `/projects/${projectId}/scenarios/${scenario.id}/edit`,
                      )
                    }
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit situation
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setConfirmingDelete(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete scenario
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null
        }
      />
      {confirmingDelete && (
        <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this scenario?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes "{scenario.name}" and every cast persona's
                walkthroughs, findings, and briefs tied to it. This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => deleteMutation.mutate({ id: scenario.id })}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete scenario"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {castOpen && (
        <CastPersonaDialog
          open={castOpen}
          onOpenChange={setCastOpen}
          projectId={projectId}
          outlineId={scenario.id}
          excludePersonaIds={alreadyCastPersonaIds}
        />
      )}
    </>
  );
}
